import { useState, useEffect, useCallback } from 'react';
import { api } from './api';

// ── HELPERS ───────────────────────────────────────────────────────────────────
const fmt = n => n == null ? '—' : '€ ' + Number(n).toLocaleString('de-AT');
const healthColor = h => ({ 'sehr gut': '#7ab87a', 'gut': '#b49656', 'kritisch': '#e07070' }[h] || 'rgba(221,216,207,0.3)');
const urgencyColor = u => ({ high: '#e07070', medium: '#b49656' }[u] || '#7ab87a');
const scoreColor = s => s >= 70 ? '#7ab87a' : s >= 40 ? '#b49656' : '#e07070';

function Bar({ value, max = 100, color = '#b49656' }) {
  return (
    <div style={{ width: '100%', height: 3, background: 'rgba(255,255,255,0.07)', borderRadius: 2 }}>
      <div style={{ width: `${Math.min((value / max) * 100, 100)}%`, height: '100%', background: color, borderRadius: 2, transition: 'width 1s ease' }} />
    </div>
  );
}

function ScoreRing({ score, size = 80 }) {
  const r = size / 2 - 8;
  const circ = 2 * Math.PI * r;
  const filled = (score / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={scoreColor(score)} strokeWidth="6"
        strokeDasharray={`${filled} ${circ}`} strokeLinecap="round" />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="middle"
        style={{ transform: `rotate(90deg) translate(0px, -${size}px)`, fill: scoreColor(score), fontSize: size * 0.22, fontFamily: 'Cormorant Garamond, serif' }}>
        {score}
      </text>
    </svg>
  );
}

// ── CLIENT FORM ───────────────────────────────────────────────────────────────
function ClientForm({ onSave, onClose, initial }) {
  const [form, setForm] = useState(initial || {
    name: '', city: '', type: 'Orchester', status: 'aktiv',
    auslastung: '', revenue_mtd: '', revenue_ziel: '',
    next_event_name: '', next_event_days: '', health: 'gut',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  async function submit() {
    try {
      const payload = {
        ...form,
        auslastung: form.auslastung ? Number(form.auslastung) : null,
        revenue_mtd: form.revenue_mtd ? Number(form.revenue_mtd) : null,
        revenue_ziel: form.revenue_ziel ? Number(form.revenue_ziel) : null,
        next_event_days: form.next_event_days ? Number(form.next_event_days) : null,
      };
      await onSave(payload);
      onClose();
    } catch (e) {
      alert('Fehler: ' + e.message);
    }
  }

  const fields = [
    { k: 'name', label: 'Name *', span: 2 },
    { k: 'city', label: 'Stadt' },
    { k: 'type', label: 'Typ', type: 'select', options: ['Orchester','Oper','Theater','Festival','Mehrspartenhaus','Konzerthaus'] },
    { k: 'status', label: 'Status', type: 'select', options: ['aktiv','onboarding','inaktiv'] },
    { k: 'health', label: 'Health', type: 'select', options: ['sehr gut','gut','kritisch'] },
    { k: 'auslastung', label: 'Auslastung %', type: 'number' },
    { k: 'revenue_mtd', label: 'Revenue MTD €', type: 'number' },
    { k: 'revenue_ziel', label: 'Revenue Ziel €', type: 'number' },
    { k: 'next_event_name', label: 'Nächstes Event' },
    { k: 'next_event_days', label: 'In X Tagen', type: 'number' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#0f1214', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 28, width: 520, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, color: '#ddd8cf', marginBottom: 20 }}>
          {initial ? 'Kunde bearbeiten' : 'Neuer Kunde'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          {fields.map(f => (
            <div key={f.k} style={{ gridColumn: f.span === 2 ? '1/-1' : undefined }}>
              <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', marginBottom: 5, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{f.label}</div>
              {f.type === 'select' ? (
                <select value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={{ width: '100%' }}>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type || 'text'} value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={{ width: '100%' }} />
              )}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={submit}>Speichern</button>
        </div>
      </div>
    </div>
  );
}

// ── ACTION FORM ───────────────────────────────────────────────────────────────
function ActionForm({ clients, onSave, onClose }) {
  const [form, setForm] = useState({
    client_id: clients[0]?.id || '',
    urgency: 'medium', title: '', detail: '', expected: '', channel: '', effort: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  async function submit() {
    if (!form.title) return alert('Titel erforderlich');
    await onSave({ ...form, urgency_order: form.urgency === 'high' ? 1 : 2 });
    onClose();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#0f1214', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 28, width: 480 }}>
        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, color: '#ddd8cf', marginBottom: 20 }}>Neue Aktion</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
          {[
            { k: 'client_id', label: 'Kunde', type: 'select', options: clients.map(c => ({ v: c.id, l: c.name })) },
            { k: 'urgency', label: 'Dringlichkeit', type: 'select', options: [{ v: 'high', l: 'Dringend' }, { v: 'medium', l: 'Empfohlen' }] },
            { k: 'title', label: 'Titel *' },
            { k: 'detail', label: 'Detail', multiline: true },
            { k: 'expected', label: 'Erwartete Wirkung (z.B. +8% Auslastung)' },
            { k: 'channel', label: 'Kanal (z.B. Email, Instagram)' },
            { k: 'effort', label: 'Aufwand (z.B. 5 Min)' },
          ].map(f => (
            <div key={f.k}>
              <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', marginBottom: 5, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{f.label}</div>
              {f.type === 'select' ? (
                <select value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={{ width: '100%' }}>
                  {f.options.map(o => typeof o === 'string'
                    ? <option key={o} value={o}>{o}</option>
                    : <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              ) : f.multiline ? (
                <textarea rows={2} value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={{ width: '100%' }} />
              ) : (
                <input value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={{ width: '100%' }} />
              )}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={submit}>Speichern</button>
        </div>
      </div>
    </div>
  );
}

// ── AUDIT GENERATOR ───────────────────────────────────────────────────────────
function AuditGenerator() {
  const [form, setForm] = useState({ name: '', type: 'Orchester', city: '', website: '' });
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState('');
  const [result, setResult] = useState(null);
  const [audits, setAudits] = useState([]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const phases = ['Website & Digital Presence...', 'Social Media Performance...', 'Newsletter & Email...', 'SEO & Sichtbarkeit...', 'Ticketing & Conversion...', 'Befund wird generiert...'];

  useEffect(() => { api.getAudits().then(setAudits).catch(() => {}); }, []);

  async function run() {
    if (!form.name.trim()) return;
    setLoading(true); setResult(null);
    for (const p of phases) { setPhase(p); await new Promise(r => setTimeout(r, 800)); }
    try {
      const data = await api.generateAudit(form);
      setResult(data);
      setAudits(prev => [data, ...prev]);
    } catch (e) { alert('Fehler: ' + e.message); }
    setLoading(false); setPhase('');
  }

  return (
    <div className="fade">
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, color: '#ddd8cf', marginBottom: 4 }}>Audit Generator</div>
        <div style={{ fontSize: 12, color: 'rgba(221,216,207,0.35)' }}>Professioneller Digital-Befund · Basis für den Erstauftrag</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>Neue Institution analysieren</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              {[
                { k: 'name', label: 'Name *', placeholder: 'z.B. Tonhalle Zürich', span: 2 },
                { k: 'city', label: 'Stadt', placeholder: 'z.B. Zürich' },
                { k: 'type', label: 'Typ', type: 'select', options: ['Orchester','Oper','Theater','Festival','Mehrspartenhaus','Konzerthaus'] },
                { k: 'website', label: 'Website (optional)', placeholder: 'tonhalle.ch' },
              ].map(f => (
                <div key={f.k} style={{ gridColumn: f.span === 2 ? '1/-1' : undefined }}>
                  <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', marginBottom: 5 }}>{f.label}</div>
                  {f.type === 'select' ? (
                    <select value={form[f.k]} onChange={e => set(f.k, e.target.value)} style={{ width: '100%' }}>
                      {f.options.map(o => <option key={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input value={form[f.k]} onChange={e => set(f.k, e.target.value)} placeholder={f.placeholder} style={{ width: '100%' }}
                      onKeyDown={e => e.key === 'Enter' && run()} />
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={run} disabled={loading || !form.name.trim()} style={{ padding: '9px 24px' }}>
                {loading ? 'Analysiere...' : 'Audit starten →'}
              </button>
            </div>
          </div>

          {loading && (
            <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
              <div style={{ marginBottom: 16 }}>
                {phases.map((p, i) => (
                  <div key={i} style={{ width: `${(i+1)*15}%`, height: 2, background: phases.indexOf(phase) > i ? '#b49656' : 'rgba(255,255,255,0.06)', borderRadius: 2, marginBottom: 4, transition: 'background 0.5s' }} />
                ))}
              </div>
              <div style={{ fontSize: 13, color: '#b49656' }} className="blink">{phase}</div>
              <div style={{ fontSize: 11, color: 'rgba(221,216,207,0.25)', marginTop: 6 }}>{form.name}</div>
            </div>
          )}

          {result?.audit_data && (
            <div className="fade">
              <div className="card" style={{ marginBottom: 12, borderColor: `${scoreColor(result.audit_data.gesamtscore)}30` }}>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                  <ScoreRing score={result.audit_data.gesamtscore} size={90} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, color: '#ddd8cf' }}>{result.institution_name}</div>
                      <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 3, background: 'rgba(180,150,90,0.15)', color: '#b49656', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Potenzial: {result.audit_data.potenzial}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: 'rgba(221,216,207,0.6)', lineHeight: 1.7, marginBottom: 8 }}>{result.audit_data.zusammenfassung}</div>
                    <div style={{ fontSize: 12, color: '#7ab87a' }}>{result.audit_data.umsatzpotenzial}</div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                {result.audit_data.bereiche?.map((b, i) => (
                  <div key={i} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: '#ddd8cf' }}>{b.name}</span>
                      <span style={{ fontSize: 13, color: scoreColor(b.score), fontFamily: 'Cormorant Garamond, serif' }}>{b.score}</span>
                    </div>
                    <Bar value={b.score} color={scoreColor(b.score)} />
                    <div style={{ fontSize: 11, color: 'rgba(221,216,207,0.4)', marginTop: 8, lineHeight: 1.6 }}>{b.befund}</div>
                    <div style={{ fontSize: 11, color: '#b49656', marginTop: 6 }}>→ {b.massnahme}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="card">
                  <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Top 3 Sofortmaßnahmen</div>
                  {result.audit_data.sofortmassnahmen?.map((m, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                      <span style={{ color: '#b49656', fontSize: 12, flexShrink: 0 }}>{i+1}.</span>
                      <span style={{ fontSize: 12, color: 'rgba(221,216,207,0.6)', lineHeight: 1.6 }}>{m}</span>
                    </div>
                  ))}
                </div>
                <div className="card" style={{ borderColor: 'rgba(180,150,90,0.2)', background: 'rgba(180,150,90,0.03)' }}>
                  <div style={{ fontSize: 10, color: 'rgba(180,150,90,0.5)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Kulturfunnel Empfehlung</div>
                  <div style={{ fontSize: 13, color: 'rgba(221,216,207,0.7)', lineHeight: 1.7, marginBottom: 16 }}>{result.audit_data.empfehlung}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" onClick={() => api.updateAuditStatus(result.id, 'angebot_gesendet')}>Angebot gesendet</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>Audit Pipeline</div>
          {audits.length === 0 ? (
            <div style={{ fontSize: 12, color: 'rgba(221,216,207,0.2)', textAlign: 'center', padding: '20px 0' }}>Noch keine Audits</div>
          ) : audits.map(a => (
            <div key={a.id} style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 5, border: '1px solid rgba(255,255,255,0.05)', marginBottom: 8, cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#ddd8cf' }}>{a.institution_name}</span>
                <span style={{ fontSize: 13, color: scoreColor(a.gesamtscore), fontFamily: 'Cormorant Garamond, serif' }}>{a.gesamtscore}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
                <span style={{ color: 'rgba(221,216,207,0.25)' }}>{new Date(a.created_at).toLocaleDateString('de-AT')}</span>
                <span style={{ color: a.status === 'in_gespraech' ? '#b49656' : a.status === 'neu' ? '#7ab87a' : 'rgba(221,216,207,0.35)' }}>
                  {a.status?.replace('_', ' ')}
                </span>
              </div>
            </div>
          ))}
          {audits.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: 11, color: 'rgba(221,216,207,0.4)', lineHeight: 1.9 }}>
              <div>{audits.length} Audits gesamt</div>
              <div style={{ color: '#b49656' }}>{audits.filter(a => a.status === 'in_gespraech' || a.status === 'angebot_gesendet').length} in Pipeline</div>
              <div style={{ color: '#7ab87a' }}>Ø Score: {Math.round(audits.reduce((s,a) => s + (a.gesamtscore||0), 0) / audits.length)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [tab, setTab] = useState('command');
  const [clients, setClients] = useState([]);
  const [actions, setActions] = useState([]);
  const [insights, setInsights] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [time, setTime] = useState(new Date());
  const [ticker, setTicker] = useState(0);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [showActionForm, setShowActionForm] = useState(false);

  const tickers = ['AIOS aktiv — alle Systeme OK', 'Analysiere Kundendaten...', 'Cross-Learning aktiv', 'Nächste Empfehlung wird berechnet...'];

  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { const t = setInterval(() => setTicker(i => (i+1) % tickers.length), 4000); return () => clearInterval(t); }, []);

  const loadData = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([api.getClients(), api.getActions()]);
      setClients(c); setActions(a);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function markDone(id) {
    await api.markActionDone(id);
    setActions(prev => prev.filter(a => a.id !== id));
  }

  async function saveClient(data) {
    if (editClient) {
      await api.updateClient(editClient.id, data);
    } else {
      await api.createClient(data);
    }
    setEditClient(null);
    loadData();
  }

  async function saveAction(data) {
    await api.createAction(data);
    loadData();
  }

  async function askAI() {
    if (!aiQuery.trim()) return;
    setAiLoading(true); setAiResponse(null);
    try {
      const { response } = await api.analyse(aiQuery);
      setAiResponse(response);
    } catch (e) { setAiResponse('Fehler: ' + e.message); }
    setAiLoading(false);
  }

  async function loadInsights() {
    setInsightsLoading(true);
    try {
      const { insights } = await api.generateInsights();
      setInsights(insights);
    } catch (e) { console.error(e); }
    setInsightsLoading(false);
  }

  useEffect(() => { if (clients.length > 0) loadInsights(); }, [clients.length]);

  const filteredActions = selectedClient ? actions.filter(a => a.client_id === selectedClient) : actions;
  const urgentCount = actions.filter(a => a.urgency === 'high').length;
  const activeClients = clients.filter(c => c.status === 'aktiv');
  const avgAuslastung = activeClients.length ? Math.round(activeClients.reduce((s,c) => s + (c.auslastung||0), 0) / activeClients.length) : 0;

  const navTabs = [
    { id: 'command', label: 'Command Center' },
    { id: 'clients', label: 'Kunden' },
    { id: 'actions', label: `Aktionen (${actions.length})` },
    { id: 'ai', label: 'AI Analyse' },
    { id: 'audit', label: '🔍 Audit Generator' },
    { id: 'performance', label: 'Performance' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#080a0c', color: '#ddd8cf', fontFamily: "'DM Mono', 'Courier New', monospace", fontSize: 13 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&family=Cormorant+Garamond:wght@400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 3px; } ::-webkit-scrollbar-thumb { background: rgba(180,150,90,0.3); }
        .card { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06); border-radius: 8px; padding: 18px; transition: border-color 0.2s; }
        .card:hover { border-color: rgba(180,150,90,0.15); }
        .btn { border: none; cursor: pointer; font-family: inherit; font-size: 11px; padding: 6px 14px; border-radius: 3px; letter-spacing: 0.04em; transition: all 0.15s; }
        .btn-primary { background: #b49656; color: #080a0c; font-weight: 600; }
        .btn-primary:hover { background: #c4a666; }
        .btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
        .btn-secondary { background: transparent; color: rgba(221,216,207,0.4); border: 1px solid rgba(221,216,207,0.1); }
        .btn-secondary:hover { border-color: rgba(221,216,207,0.3); color: rgba(221,216,207,0.7); }
        .tab { background: transparent; border: none; border-bottom: 2px solid transparent; cursor: pointer; font-family: inherit; font-size: 11px; padding: 8px 16px; color: rgba(221,216,207,0.35); letter-spacing: 0.06em; text-transform: uppercase; transition: all 0.15s; }
        .tab.active { color: #b49656; border-bottom-color: #b49656; }
        .tab:hover:not(.active) { color: rgba(221,216,207,0.6); }
        .client-row { display: flex; align-items: center; padding: 10px 12px; border-radius: 6px; cursor: pointer; transition: background 0.15s; gap: 12px; }
        .client-row:hover { background: rgba(255,255,255,0.04); }
        .fade { animation: fadeIn 0.3s ease; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .blink { animation: blink 1.5s infinite; }
        @keyframes blink { 0%,100%{opacity:1;} 50%{opacity:0.3;} }
        .pulse-dot { display:inline-block;width:5px;height:5px;border-radius:50%;background:#7ab87a;animation:pulse 2s infinite;margin-right:5px;vertical-align:middle; }
        @keyframes pulse { 0%,100%{transform:scale(1);opacity:1;} 50%{transform:scale(1.4);opacity:0.5;} }
        input, select, textarea { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 5px; color: #ddd8cf; font-family: inherit; font-size: 13px; padding: 8px 10px; outline: none; transition: border-color 0.2s; }
        input:focus, select:focus, textarea:focus { border-color: rgba(180,150,90,0.4); }
        textarea { resize: vertical; }
        .ai-response { background: rgba(180,150,90,0.04); border: 1px solid rgba(180,150,90,0.15); border-radius: 6px; padding: 16px; font-size: 13px; line-height: 1.8; color: rgba(221,216,207,0.8); white-space: pre-wrap; }
      `}</style>

      {showClientForm && <ClientForm onSave={saveClient} onClose={() => { setShowClientForm(false); setEditClient(null); }} initial={editClient} />}
      {showActionForm && <ActionForm clients={clients} onSave={saveAction} onClose={() => setShowActionForm(false)} />}

      {/* Top Bar */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '0 28px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: 'rgba(8,10,12,0.97)', backdropFilter: 'blur(10px)', zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, color: '#b49656', letterSpacing: '0.05em' }}>KULTURFUNNEL</span>
          <span style={{ fontSize: 10, color: 'rgba(221,216,207,0.2)', letterSpacing: '0.15em' }}>AIOS · INTERN</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, fontSize: 11 }}>
          <div style={{ color: 'rgba(221,216,207,0.3)' }}>
            <span className="pulse-dot" />{tickers[ticker]}
          </div>
          <span style={{ color: 'rgba(221,216,207,0.25)', fontVariantNumeric: 'tabular-nums' }}>
            {time.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
          {urgentCount > 0 && (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#e07070' }} />
              <span style={{ color: '#e07070', fontSize: 10 }}>{urgentCount} DRINGEND</span>
            </div>
          )}
        </div>
      </div>

      {/* Nav */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '0 28px', display: 'flex', gap: 0 }}>
        {navTabs.map(t => (
          <button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      <div style={{ padding: '24px 28px', maxWidth: 1200 }}>

        {/* COMMAND CENTER */}
        {tab === 'command' && (
          <div className="fade">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Aktive Kunden', value: activeClients.length, sub: `${clients.filter(c=>c.status==='onboarding').length} Onboarding` },
                { label: 'Offene Aktionen', value: actions.length, sub: `${urgentCount} dringend`, alert: urgentCount > 0 },
                { label: 'Ø Auslastung', value: `${avgAuslastung}%`, sub: 'Ziel: 75%' },
                { label: 'Revenue MTD', value: fmt(activeClients.reduce((s,c)=>s+(c.revenue_mtd||0),0)), sub: 'alle Kunden' },
                { label: 'AI Insights', value: insights.length, sub: insightsLoading ? 'wird generiert...' : 'aktuell' },
              ].map((k, i) => (
                <div key={i} className="card">
                  <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>{k.label}</div>
                  <div style={{ fontSize: 24, fontFamily: 'Cormorant Garamond, serif', color: k.alert ? '#e07070' : '#ddd8cf', marginBottom: 4 }}>{k.value}</div>
                  <div style={{ fontSize: 10, color: k.alert ? 'rgba(224,112,112,0.6)' : 'rgba(221,216,207,0.25)' }}>{k.sub}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Kunden</div>
                  <button className="btn btn-secondary" style={{ fontSize: 10 }} onClick={() => { setEditClient(null); setShowClientForm(true); }}>+ Neu</button>
                </div>
                {clients.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'rgba(221,216,207,0.2)', padding: '20px 0', textAlign: 'center' }}>
                    Noch keine Kunden. Füge deinen ersten Kunden hinzu.
                  </div>
                ) : clients.map(c => (
                  <div key={c.id} className="client-row" onClick={() => { setSelectedClient(c.id); setTab('actions'); }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: healthColor(c.health), flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: '#ddd8cf', marginBottom: 3 }}>{c.name}</div>
                      {c.auslastung != null ? <Bar value={c.auslastung} color={c.auslastung < 40 ? '#e07070' : c.auslastung > 75 ? '#7ab87a' : '#b49656'} /> : <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.2)' }}>Onboarding</div>}
                    </div>
                    {c.auslastung != null && <span style={{ fontSize: 11, color: c.auslastung < 40 ? '#e07070' : c.auslastung > 75 ? '#7ab87a' : '#b49656' }}>{c.auslastung}%</span>}
                  </div>
                ))}
              </div>

              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>AI Insights</div>
                  <button className="btn btn-secondary" style={{ fontSize: 10 }} onClick={loadInsights} disabled={insightsLoading}>
                    {insightsLoading ? '...' : '↻ Aktualisieren'}
                  </button>
                </div>
                {insights.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'rgba(221,216,207,0.2)', padding: '20px 0', textAlign: 'center' }}>
                    {clients.length === 0 ? 'Kunden hinzufügen um Insights zu erhalten' : insightsLoading ? 'Wird generiert...' : 'Keine Insights'}
                  </div>
                ) : insights.map((ins, i) => (
                  <div key={i} style={{ padding: '10px 12px', borderRadius: 5, marginBottom: 8, borderLeft: `2px solid ${ins.type === 'warning' ? '#e07070' : ins.type === 'opportunity' ? '#b49656' : '#7ab87a'}`, background: ins.type === 'warning' ? 'rgba(224,112,112,0.04)' : ins.type === 'opportunity' ? 'rgba(180,150,90,0.04)' : 'rgba(122,184,122,0.04)' }}>
                    <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', marginBottom: 4 }}>{ins.client}</div>
                    <div style={{ fontSize: 12, color: 'rgba(221,216,207,0.7)', lineHeight: 1.6 }}>{ins.text}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Urgent actions */}
            {actions.filter(a => a.urgency === 'high').length > 0 && (
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Dringendste Aktionen</div>
                  <button className="btn btn-secondary" onClick={() => setTab('actions')}>Alle →</button>
                </div>
                {actions.filter(a => a.urgency === 'high').slice(0, 3).map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', marginBottom: 8, background: 'rgba(224,112,112,0.04)', border: '1px solid rgba(224,112,112,0.12)', borderRadius: 5 }}>
                    <div>
                      <span style={{ fontSize: 10, color: '#e07070', marginRight: 10 }}>▲ {a.clients?.name}</span>
                      <span style={{ fontSize: 13, color: '#ddd8cf' }}>{a.title}</span>
                      {a.effort && <span style={{ fontSize: 11, color: 'rgba(221,216,207,0.3)', marginLeft: 12 }}>{a.effort}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {a.expected && <span style={{ fontSize: 11, color: '#7ab87a' }}>{a.expected}</span>}
                      <button className="btn btn-primary" onClick={() => markDone(a.id)}>Erledigt</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CLIENTS */}
        {tab === 'clients' && (
          <div className="fade">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, color: '#ddd8cf', marginBottom: 4 }}>Kunden</div>
                <div style={{ fontSize: 12, color: 'rgba(221,216,207,0.35)' }}>{clients.length} Institutionen erfasst</div>
              </div>
              <button className="btn btn-primary" onClick={() => { setEditClient(null); setShowClientForm(true); }}>+ Neuer Kunde</button>
            </div>
            {clients.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 14, color: 'rgba(221,216,207,0.3)', marginBottom: 16 }}>Noch keine Kunden erfasst</div>
                <button className="btn btn-primary" onClick={() => { setEditClient(null); setShowClientForm(true); }}>Ersten Kunden hinzufügen</button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                {clients.map(c => (
                  <div key={c.id} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                      <div>
                        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, color: '#ddd8cf', marginBottom: 3 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: 'rgba(221,216,207,0.3)' }}>{c.type} · {c.city}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 3, background: `${healthColor(c.health)}18`, color: healthColor(c.health), letterSpacing: '0.06em', textTransform: 'uppercase' }}>{c.health || c.status}</span>
                        <button className="btn btn-secondary" style={{ fontSize: 10, padding: '4px 10px' }} onClick={() => { setEditClient(c); setShowClientForm(true); }}>✎</button>
                      </div>
                    </div>
                    {c.auslastung != null && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
                          <span style={{ color: 'rgba(221,216,207,0.4)' }}>Auslastung</span>
                          <span style={{ color: healthColor(c.health) }}>{c.auslastung}%</span>
                        </div>
                        <Bar value={c.auslastung} color={healthColor(c.health)} />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginTop: 14 }}>
                          {[
                            { label: 'Revenue MTD', value: fmt(c.revenue_mtd) },
                            { label: 'Ziel', value: fmt(c.revenue_ziel) },
                            { label: 'Nächstes Event', value: c.next_event_name ? `${c.next_event_name} (${c.next_event_days}d)` : '—' },
                          ].map((s,i) => (
                            <div key={i}><div style={{ fontSize: 10, color: 'rgba(221,216,207,0.25)', marginBottom: 3 }}>{s.label}</div><div style={{ fontSize: 12, color: '#ddd8cf' }}>{s.value}</div></div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ACTIONS */}
        {tab === 'actions' && (
          <div className="fade">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className={`btn ${!selectedClient ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSelectedClient(null)}>Alle ({actions.length})</button>
                {clients.map(c => {
                  const n = actions.filter(a => a.client_id === c.id).length;
                  return <button key={c.id} className={`btn ${selectedClient===c.id?'btn-primary':'btn-secondary'}`} onClick={() => setSelectedClient(selectedClient===c.id?null:c.id)}>{c.name.split(' ')[0]} {n > 0 ? `(${n})` : ''}</button>;
                })}
              </div>
              <button className="btn btn-primary" onClick={() => setShowActionForm(true)}>+ Aktion</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filteredActions.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'rgba(221,216,207,0.2)' }}>
                  {actions.length === 0 ? 'Noch keine Aktionen erfasst' : 'Alle Aktionen erledigt ✓'}
                </div>
              ) : filteredActions.map(a => (
                <div key={a.id} className="card" style={{ borderLeft: `3px solid ${urgencyColor(a.urgency)}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 7, flexWrap: 'wrap', fontSize: 10 }}>
                        <span style={{ color: urgencyColor(a.urgency), letterSpacing: '0.06em', textTransform: 'uppercase' }}>{a.urgency === 'high' ? '▲ Dringend' : '◆ Empfohlen'}</span>
                        {a.clients?.name && <span style={{ color: 'rgba(221,216,207,0.3)' }}>{a.clients.name}</span>}
                        {a.channel && <><span style={{ color: 'rgba(221,216,207,0.2)' }}>·</span><span style={{ color: 'rgba(221,216,207,0.3)' }}>{a.channel}</span></>}
                        {a.effort && <><span style={{ color: 'rgba(221,216,207,0.2)' }}>·</span><span style={{ color: 'rgba(221,216,207,0.3)' }}>⏱ {a.effort}</span></>}
                      </div>
                      <div style={{ fontSize: 14, color: '#ddd8cf', marginBottom: 5 }}>{a.title}</div>
                      {a.detail && <div style={{ fontSize: 12, color: 'rgba(221,216,207,0.45)', lineHeight: 1.6 }}>{a.detail}</div>}
                    </div>
                    {a.expected && (
                      <div style={{ textAlign: 'right', marginLeft: 20, flexShrink: 0 }}>
                        <div style={{ fontSize: 15, color: '#7ab87a', fontFamily: 'Cormorant Garamond, serif' }}>{a.expected}</div>
                        <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.2)' }}>Erwartung</div>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.04)', display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" onClick={() => markDone(a.id)}>✓ Erledigt</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI ANALYSE */}
        {tab === 'ai' && (
          <div className="fade">
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, color: '#ddd8cf', marginBottom: 6 }}>AI Analyse</div>
              <div style={{ fontSize: 12, color: 'rgba(221,216,207,0.35)' }}>Das AIOS kennt alle Kundendaten und antwortet auf Deutsch.</div>
            </div>
            <div className="card" style={{ marginBottom: 16 }}>
              <textarea rows={3} placeholder="z.B. 'Welcher Kunde braucht heute meine Aufmerksamkeit?' oder 'Was ist die beste nächste Aktion für [Kunde]?'" value={aiQuery} onChange={e => setAiQuery(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) askAI(); }} style={{ width: '100%', marginBottom: 10 }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: 'rgba(221,216,207,0.2)' }}>⌘ + Enter zum Senden</span>
                <button className="btn btn-primary" onClick={askAI} disabled={aiLoading}>{aiLoading ? 'Analysiere...' : 'Analysieren →'}</button>
              </div>
            </div>
            {aiLoading && <div style={{ padding: 20, textAlign: 'center', color: 'rgba(221,216,207,0.3)' }}><span className="blink">AIOS denkt nach...</span></div>}
            {aiResponse && <div className="card fade" style={{ marginBottom: 16 }}><div style={{ fontSize: 10, color: 'rgba(180,150,90,0.6)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>AIOS Antwort</div><div className="ai-response">{aiResponse}</div></div>}
            <div>
              <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.25)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Schnellfragen</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {['Welcher Kunde braucht heute meine Aufmerksamkeit am dringendsten?', 'Wo verliere ich gerade Geld?', 'Welche Chancen verpasse ich gerade?', 'Was soll ich als erstes tun heute?'].map((q,i) => (
                  <button key={i} className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setAiQuery(q)}>{q}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* AUDIT GENERATOR */}
        {tab === 'audit' && <AuditGenerator />}

        {/* PERFORMANCE */}
        {tab === 'performance' && (
          <div className="fade">
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, color: '#ddd8cf', marginBottom: 6 }}>Performance</div>
              <div style={{ fontSize: 12, color: 'rgba(221,216,207,0.35)' }}>Agentur-Gesamtbild</div>
            </div>
            {activeClients.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 14, color: 'rgba(221,216,207,0.3)' }}>Noch keine Daten. Füge Kunden hinzu.</div>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 16 }}>
                  {activeClients.map(c => (
                    <div key={c.id} className="card">
                      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 16, color: '#ddd8cf', marginBottom: 4 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: 'rgba(221,216,207,0.3)', marginBottom: 12 }}>{c.city}</div>
                      {c.auslastung != null && (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}><span style={{ color: 'rgba(221,216,207,0.4)' }}>Auslastung</span><span style={{ color: healthColor(c.health) }}>{c.auslastung}%</span></div>
                          <Bar value={c.auslastung} color={healthColor(c.health)} />
                        </>
                      )}
                      {c.revenue_mtd != null && c.revenue_ziel != null && (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5, marginTop: 10 }}><span style={{ color: 'rgba(221,216,207,0.4)' }}>Revenue vs. Ziel</span><span style={{ color: 'rgba(221,216,207,0.6)' }}>{Math.round((c.revenue_mtd/c.revenue_ziel)*100)}%</span></div>
                          <Bar value={c.revenue_mtd} max={c.revenue_ziel} color="#b49656" />
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11, marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                            <div><div style={{ color: 'rgba(221,216,207,0.25)', marginBottom: 2 }}>MTD</div><div style={{ color: '#ddd8cf' }}>{fmt(c.revenue_mtd)}</div></div>
                            <div><div style={{ color: 'rgba(221,216,207,0.25)', marginBottom: 2 }}>Ziel</div><div style={{ color: 'rgba(221,216,207,0.4)' }}>{fmt(c.revenue_ziel)}</div></div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                <div className="card">
                  <div style={{ fontSize: 10, color: 'rgba(221,216,207,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>Agentur Gesamt</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
                    {[
                      { v: activeClients.length, l: 'Aktive Kunden' },
                      { v: `${avgAuslastung}%`, l: 'Ø Auslastung' },
                      { v: fmt(activeClients.reduce((s,c)=>s+(c.revenue_mtd||0),0)), l: 'Revenue MTD gesamt' },
                      { v: fmt(activeClients.reduce((s,c)=>s+(c.revenue_ziel||0),0)), l: 'Ziel MTD gesamt' },
                    ].map((s,i) => (
                      <div key={i} style={{ textAlign: 'center', padding: '14px 0' }}>
                        <div style={{ fontSize: 26, color: '#b49656', fontFamily: 'Cormorant Garamond, serif', marginBottom: 6 }}>{s.v}</div>
                        <div style={{ fontSize: 12, color: '#ddd8cf' }}>{s.l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
